import { Location } from '@angular/common';
import {
	ApplicationRef,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	signal,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivationEnd, Router } from '@angular/router';
import {
	EmbedCard,
	readEmbedCard,
	readEmbedViewerToken,
} from './utils/embed-context.util';
import { embedCardLayout } from '@framework/analytics/utils/embed-card-layout.util';
import { JeptoLayoutContainerComponent } from '@framework/analytics/components/containers/jepto-layout-container/jepto-layout-container.component';
import { CardConfig, CardType } from '@framework/analytics/models/card-model';
import type { FilterCardConfig } from '@framework/analytics/models/card-model';
import { ChartCardConfig } from '@framework/analytics/models/chart-config.model';
import type {
	CardList,
	LayoutConfig,
} from '@framework/analytics/models/layout.model';
import {
	requirePageLayout,
	type PageConfig,
} from '@framework/analytics/models/page.model';
import { PaletteConfig } from '@framework/analytics/models/palette.model';
import {
	BUILT_IN_PALETTES,
	isBuiltInPaletteId,
} from '@framework/analytics/defaults/palette.default';
import {
	DEFAULT_THEME,
	isBuiltInThemeId,
} from '@framework/analytics/defaults/theme.defaults';
import { ThemeConfig } from '@framework/analytics/models/theme.model';
import { ViewConfig } from '@framework/analytics/models/view.model';
import {
	clientBrandingFromViewAccess,
	isViewAccessActive,
	type ViewAccessPublic,
} from '@framework/analytics/models/view-access.model';
import { CardService } from '@framework/analytics/services/card.service';
import { FilterService } from '@framework/analytics/services/filter.service';
import { PageFilterService } from '@framework/analytics/services/page-filter.service';
import { CardRuntimeRefreshStateService } from '@framework/analytics/services/card-runtime-refresh-state.service';
import { CardRuntimeDataRefreshBroker } from '@framework/analytics/services/card-runtime-data-refresh.broker';
import { PageDataRefreshCoordinatorService } from '@framework/analytics/services/page-data-refresh-coordinator.service';
import {
	AnalyticsDataLatencyService,
	dataSourceServicesFromCardConfig,
} from '@framework/analytics/services/analytics-data-latency.service';
import {
	AnalyticsDataRequesterService,
	webSocketRequesterFromEvent,
} from '@framework/analytics/services/analytics-data-requester.service';
import {
	isDropdownFilterControlType,
	resolveFilterControlType,
} from '@framework/analytics/utils/filter-control-type.util';
import { resolveEncodedReportIds } from '@framework/analytics/utils/report-id-encoding.util';
import { LayoutService } from '@framework/analytics/services/layout.service';
import { PageService } from '@framework/analytics/services/page.service';
import { PaletteService } from '@framework/analytics/services/palette.service';
import { ThemeService } from '@framework/analytics/services/theme.service';
import { debounceTime, filter, from, map, switchMap, tap } from 'rxjs';
import { AnalyticsService } from './services/analytics.service';
import { environment } from '@analytics/environments/environment';
import { RequestService } from './services/request.service';
import {
	ViewAccessService,
	type ViewAccessLookup,
} from './services/view-access.service';
import { ViewerAuthService } from './services/viewer-auth.service';
import { ReportAccessGateComponent } from './components/report-access-gate/report-access-gate.component';
import { SyncService, SyncEvent } from './services/sync.service';
import { JeptoAnalyticsUtil } from './util/jepto-analytics.util';

import { JeptoViewContainerComponent } from '@framework/analytics/components/containers/jepto-view-container/jepto-view-container.component';
import { ChartDataResponse } from '@framework/analytics/models/card-model';
import type { FetchPageDataOptions } from '@framework/analytics/models/fetch-page-data-options.model';
import { DataService } from '@framework/analytics/services/data.service';
import {
	applyTableWebSocketDataToDataService,
	hasRenderableTablePayload,
	parseAppSyncCardEvent,
	tableDataFromChartDataResponse,
} from '@framework/analytics/components/editors/jepto-table-editor/services/table-preview-data.util';
import { applyChartWebSocketDataToDataService } from '@framework/analytics/components/editors/jepto-chart-editor/services/chart-preview-data.util';
import {
	readWebSocketCacheStatus,
	shouldEndCardRefreshAfterWebSocket,
	shouldEndCardRefreshAfterWebSocketChartEvent,
} from '@framework/analytics/utils/ws-card-cache-status.util';
import { mergeCardDataFiltersFromApi } from '@framework/analytics/utils/card-data-gql.util';
import {
	decideCardConfigRealtimeApply,
	resolveCardLayoutPageId,
	shouldApplyCardConfigRealtimeContent,
} from '@framework/analytics/utils/card-config-realtime-apply.util';
import { LayoutStateService } from '@framework/analytics/states/layout-state.service';
import { PageStateService } from '@framework/analytics/states/page-state.service';
import {
	resolvePageGlobalDateRangeExpression,
	shouldRefetchCardDataOnGlobalDateChange,
} from '@framework/analytics/utils/chart-card-date-range.util';
import { TableCardInteractionService } from '@framework/analytics/services/table-card-interaction.service';
import { applyAnalysesDataToDataService } from '@framework/analytics/utils/analysis.util';
import { deriveLayoutIdForPage } from '@framework/analytics/utils/page-layout-id.util';
import type { DateRangeExpression } from '@framework/analytics/models/date-range-expression.model';
import type { WsCardDataEventBase } from '@framework/analytics/models/ws-card-data-event.model';
import {
	dateRangeExpressionForPageData,
	parseViewDateRangeFromApi,
	resolveReportDefaultDateRangeExpression,
} from '@jepto/features/reports/utils/view-date-range.util';
import { error, log, warn } from '@jepto/shared/utils/logger';
import {
	AnalyticsCardDataRefService,
	cardDataRefErrorMessage,
	shouldResolveCardDataRef,
} from '@framework/analytics/services/analytics-card-data-ref.service';
import { ReportCaptureReadinessService } from './services/report-capture-readiness.service';
import {
	parseDeliveryIdFromUrl,
	ReportViewTrackingService,
} from './services/report-view-tracking.service';
import { reportAnalyticsError } from './utils/analytics-error-reporting.util';
import { shouldSettleCaptureAfterWebSocket } from './utils/card-capture-readiness.util';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ReportPdfExportService } from './services/report-pdf-export.service';
import { PDF_EXPORT_GENERIC_ERROR } from '@framework/analytics/models/report-pdf-export.model';
import { JepSnackbarComponent } from '@jepto/shared/components/jep-snackbar/jep-snackbar.component';

const IDENTITY_ID_MAX_ATTEMPTS = 3;
const IDENTITY_ID_RETRY_DELAY_MS = 1000;

/**
 * Shared fields present on every WebSocket event regardless of card type.
 * Card CONFIG (metadata like title, style) comes from GraphQL.
 * WebSocket delivers the runtime DATA for each card.
 */
interface WebSocketEventBase extends WsCardDataEventBase {}

interface ChartWebSocketEvent extends WebSocketEventBase {
	cardType: 'chart';
	messageType: 'data';
	chartType?: string;
	data?: any;
}

/** Table card payloads use the same `data` shape as charts; backend sends `cardType: 'table'`. */
interface TableWebSocketEvent extends WebSocketEventBase {
	cardType: 'table';
	messageType: 'data';
	chartType?: string;
	data?: any;
}

interface ContentWebSocketEvent extends WebSocketEventBase {
	cardType: 'html' | 'markdown';
	messageType: 'data';
	content: string;
}

interface FilterWebSocketEvent extends WebSocketEventBase {
	cardType: 'filter';
	messageType: 'data';
	data: any;
}

interface ImageWebSocketEvent extends WebSocketEventBase {
	cardType: 'image';
	messageType: 'image';
	imageRef?: string;
	imageRefLight?: string;
	imageRefDark?: string;
}

/** Analysis card: runtime copy under `data.analyses`. */
interface AnalysisWebSocketEvent extends WebSocketEventBase {
	cardType: 'analysis';
	messageType: 'data';
	data?: { analyses?: unknown[] };
}

interface ConfigWebSocketEvent extends WebSocketEventBase {
	messageType: 'config';
	cardType: string;
	content?: string;
}

type WebSocketEvent =
	| ChartWebSocketEvent
	| TableWebSocketEvent
	| ContentWebSocketEvent
	| FilterWebSocketEvent
	| ImageWebSocketEvent
	| AnalysisWebSocketEvent
	| ConfigWebSocketEvent;

/** Runtime data events only — config refresh is handled in {@link handleCardConfig}. */
type DataWebSocketEvent = Exclude<WebSocketEvent, ConfigWebSocketEvent>;

/**
 * Share-gate outcome for the current `reportId`.
 * `allowed` keeps the existing guest/JWT data path; `gate` and `denied` replace the report UI.
 */
type ReportAccessState = 'checking' | 'allowed' | 'gate' | 'denied';

@Component({
	selector: 'app-root',
	imports: [
		JeptoViewContainerComponent,
		JeptoLayoutContainerComponent,
		ReportAccessGateComponent,
		MatIconModule,
		MatSnackBarModule,
		JepSnackbarComponent,
	],
	templateUrl: './app.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './app.component.scss',
})
export class AppComponent {
	// Dependency Injection
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private location = inject(Location);
	private titleService = inject(Title);
	private cardService = inject(CardService);
	private dataService = inject(DataService);
	private layoutService = inject(LayoutService);
	private pageService = inject(PageService);
	private analyticsService = inject(AnalyticsService);
	private requestService = inject(RequestService);
	private themeService = inject(ThemeService);
	private paletteService = inject(PaletteService);
	private filterService = inject(FilterService);
	private pageFilterService = inject(PageFilterService);
	private cardRuntimeRefreshState = inject(CardRuntimeRefreshStateService);
	private cardRuntimeDataRefreshBroker = inject(CardRuntimeDataRefreshBroker);
	private pageDataRefreshCoordinator = inject(
		PageDataRefreshCoordinatorService
	);
	private analyticsDataLatency = inject(AnalyticsDataLatencyService);
	private cardDataRef = inject(AnalyticsCardDataRefService);
	private analyticsDataRequester = inject(AnalyticsDataRequesterService);
	private syncService = inject(SyncService);
	private destroyRef = inject(DestroyRef);
	private appRef = inject(ApplicationRef);
	private captureReadiness = inject(ReportCaptureReadinessService);
	private reportViewTracking = inject(ReportViewTrackingService);
	private viewAccessService = inject(ViewAccessService);
	private viewerAuth = inject(ViewerAuthService);
	private tableCardInteraction = inject(TableCardInteractionService);
	private reportPdfExportService = inject(ReportPdfExportService);
	private snackBar = inject(MatSnackBar);
	private layoutState = inject(LayoutStateService);
	private pageState = inject(PageStateService);

	#cardConfigRealtimeGeneration = new Map<string, number>();

	readonly pdfExportInProgress = signal(false);

	/** Set by backend Playwright jobs via `?capture=1`. */
	readonly captureMode = this.captureReadiness.isCaptureMode;
	readonly captureReady = this.captureReadiness.captureReady;

	/**
	 * Buffer for WebSocket events that arrive before identity ID is available.
	 * These events will be processed once identity is set.
	 */
	private pendingWebSocketEvents: DataWebSocketEvent[] = [];

	// State Signals
	identityId = signal<string | null>(null);
	reportId = signal<string | null>(null);
	accountId = signal<string | null>(null);
	viewId = signal<string | null>(null);
	pageId = signal<string | null>(null);
	viewConfig = signal<ViewConfig | null>(null);
	selectedDateRange = signal<string[] | null>(null);
	pageLoading = signal<boolean>(false);
	readonly accessState = signal<ReportAccessState>('checking');
	/** MCP `view_card` (JEP-10855): one card, loaded on its own; no report or page. */
	readonly embedCard: EmbedCard | null = readEmbedCard();
	readonly embedCardLayout: LayoutConfig | null = this.embedCard
		? embedCardLayout(this.embedCard)
		: null;
	readonly gateBrandName = signal<string | undefined>(undefined);
	readonly gateBrandLogoUrl = signal<string | undefined>(undefined);

	/** Session date range from the page date-range filter card (not persisted). */
	private readonly activePageDateRangeExpression = signal<
		DateRangeExpression | undefined
	>(undefined);

	readonly reportDateRangeExpressionForView = computed(() =>
		this.resolvePageDateRangeExpression()
	);

	/**
	 * Computed signal that only provides selectedPage to ViewContainer
	 * AFTER viewConfig has loaded and contains the requested page.
	 * This prevents the race condition where ViewContainer tries to select
	 * a page before the pages array is populated.
	 */
	selectedPageForView = computed(() => {
		const config = this.viewConfig();
		const currentPageId = this.pageId();

		// Don't provide selectedPage until viewConfig is loaded with pages
		if (!config?.pages?.length || !currentPageId) {
			return undefined;
		}

		// Verify the page exists in the config before passing to ViewContainer
		const normalizedPageId = this.normalizePageId(currentPageId);
		const pageExists = config.pages.some(
			p => this.normalizePageId(p.pageId) === normalizedPageId
		);

		return pageExists ? normalizedPageId : undefined;
	});

	// Child Components (Signal-based ViewChild)
	viewComponent = viewChild<JeptoViewContainerComponent>('view');

	private rawPageFromUrl: string | undefined;
	private appSyncSubscriptionStarted = false;
	private initialParamsProcessed = false;
	/** Report whose viewer token came from `window.__JEPTO_EMBED__`, not the email code. */
	#embedOwnerTokenReportId: string | null = null;

	#pageFilterRefreshGeneration = 0;
	#pendingPageFilterRefresh: {
		requestId: string;
		generation: number;
	} | null = null;

	constructor() {
		// Initialize identity ID synchronously from cache if available,
		// otherwise fetch asynchronously. This reduces race conditions
		// where AppSync events arrive before identity is set.
		this.initializeIdentityId();

		this.setupDynamicLogo();
		if (this.embedCard) {
			void this.loadEmbedCard(this.embedCard);
		} else {
			this.listenToRouteParams();
		}
		this.setupEffects();
		this.setupPageFilterRefresh();
		this.setupCardRuntimeDataRefresh();
		this.setupDebugHelpers();
	}

	/**
	 * Card-only mode for MCP `view_card`: authenticate with the card-scoped viewer
	 * token, subscribe to the card's channel and read just this card. `getItem.card`
	 * also starts its data query, which arrives over the WebSocket like on a report.
	 */
	private async loadEmbedCard(card: EmbedCard): Promise<void> {
		this.accountId.set(card.accountId);
		this.viewId.set(card.viewId);
		this.pageId.set(card.pageId);
		// The viewer keys tokens by report; a card embed keys its token by cardId.
		this.viewerAuth.storeViewerSession('', card.cardId, card.viewerToken);
		this.requestService.setActiveReport(card.cardId);
		this.accessState.set('allowed');

		this.startAppSyncSubscription();
		try {
			await this.syncService.waitForConnection();
		} catch (err) {
			reportAnalyticsError(
				'[Analytics] WebSocket connection failed for embed card:',
				err,
				{ feature: 'analytics', operation: 'websocketConnect' }
			);
		}
		const slot = this.embedCardLayout!.cards;
		this.cardService.setCardConfig(card.cardId, {
			cardId: card.cardId,
			cardType: card.cardType,
			...(card.chartType ? { chartType: card.chartType } : {}),
		} as CardConfig);
		await this.getCardConfigs(card.accountId, card.pageId, slot);
	}

	private setupPageFilterRefresh(): void {
		this.pageFilterService.refreshIntent$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.#preparePageFilterRefresh());

		this.pageFilterService.refresh$
			.pipe(
				tap(() => this.#preparePageFilterRefresh(true)),
				debounceTime(150),
				switchMap(() => {
					const pending = this.#pendingPageFilterRefresh;
					if (!pending) {
						return from(this.refreshPageData());
					}
					return from(
						this.refreshPageData({
							requestId: pending.requestId,
							advanceWebSocketFloor: false,
							pageFilterRefreshGeneration: pending.generation,
						})
					);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/** Advance WS floor and card loaders before debounced getPageData (filter UX). */
	#preparePageFilterRefresh(skipLoaderRestart = false): void {
		const pageId = this.pageId();
		const accountId = this.accountId();
		const range = this.resolvePageDateRangeForFetch();
		if (!pageId || !accountId || !range) {
			return;
		}

		const generation = ++this.#pageFilterRefreshGeneration;
		const requestId = this.analyticsDataRequester.createRequestId();
		this.pageDataRefreshCoordinator.setRefreshFloor(pageId, requestId);

		const loadingCardIds = this.#cardIdsForPageFilterRefresh(pageId);
		const shouldBeginLoaders =
			loadingCardIds.length > 0 &&
			(!skipLoaderRestart ||
				loadingCardIds.some(
					id => !this.cardRuntimeRefreshState.isCardLoading(id)
				));
		if (shouldBeginLoaders) {
			this.cardRuntimeRefreshState.beginCardsRefresh(loadingCardIds);
			// Do not call appRef.tick() here: this runs from refresh$ during the
			// selected-page effect (same CD turn) and causes NG0101. Loaders
			// paint via beginCardsRefresh signals; refreshPageData may tick later.
		}

		this.#pendingPageFilterRefresh = { requestId, generation };
	}

	private setupCardRuntimeDataRefresh(): void {
		this.cardRuntimeDataRefreshBroker.refreshRequested$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(cardId => {
				const range = this.resolvePageDateRangeForFetch();
				if (!range) {
					this.cardRuntimeRefreshState.endCardRefresh(cardId);
					return;
				}
				void this.getCardData(cardId, range);
			});
	}

	/**
	 * Initialize identity ID, preferring cached value for immediate availability.
	 * This addresses the race condition where AppSync events arrive before
	 * the async getIdentityId() completes.
	 */
	private initializeIdentityId(): void {
		// Use scoped cache from RequestService to avoid cross-environment stale identities.
		const cachedIdentityId = this.requestService.getCachedIdentityId();
		if (cachedIdentityId) {
			this.identityId.set(cachedIdentityId);
			log('[Analytics] Identity ID loaded from cache:', cachedIdentityId);
			void this.warmRequesterCache();
			return;
		}

		// No valid cache, fetch asynchronously
		void this.fetchIdentityId();
	}

	/**
	 * Cognito GetId can fail at the network level (flaky mobile, iOS Safari
	 * backgrounding). Retry with backoff, then once more when back online.
	 */
	private async fetchIdentityId(attempt = 1): Promise<void> {
		try {
			const id = await this.requestService.getIdentityId();
			this.identityId.set(id);
			log('[Analytics] Identity ID fetched:', id);
			void this.warmRequesterCache();
		} catch (error) {
			if (attempt < IDENTITY_ID_MAX_ATTEMPTS) {
				setTimeout(
					() => void this.fetchIdentityId(attempt + 1),
					IDENTITY_ID_RETRY_DELAY_MS * attempt
				);
				return;
			}
			reportAnalyticsError(
				'[Analytics] Failed to fetch identity ID',
				error,
				{
					feature: 'analytics',
					operation: 'getIdentityId',
				}
			);
			window.addEventListener(
				'online',
				() => void this.fetchIdentityId(),
				{
					once: true,
				}
			);
		}
	}

	private warmRequesterCache(): void {
		void this.analyticsDataRequester.resolveRequester().then(() => {
			this.processPendingEvents();
		});
	}

	private setupDynamicLogo(): void {
		log(`[Analytics] Location path: ${window.location.hostname}`);
		if (window.location.hostname.includes('thejoinary.com')) {
			log('[Analytics] Changing app logo to The Joinary logo');
			JeptoAnalyticsUtil.changeAppLogo(
				'https://cdn.prod.website-files.com/617761c9331ea66a55a2d8ae/639920f7e67463f7eb9a4a18_Favicon.png'
			);
		}
	}

	private setupEffects() {
		// Keep table Grid paging getCardData aligned with page/report date range.
		effect(() => {
			const expression = this.resolvePageDateRangeExpression();
			this.tableCardInteraction.setPageDateRangeExpression(expression);
		});

		// Monitor selected page changes in the child component
		effect(() => {
			const selectedPageId = this.viewComponent()?.selectedPageIdSignal();
			const currentPageId = this.pageId();

			if (selectedPageId && currentPageId) {
				if (!this.isSamePage(selectedPageId, currentPageId)) {
					// Wrap in untracked if this causes loops, but strictly speaking effect depends on both
					this.handleSelectedPage(selectedPageId);
				}
			}
		});
	}

	private listenToRouteParams(): void {
		// Process initial route params immediately (handles case where component loads after navigation)
		const initialParams = this.route.snapshot.params;
		if (initialParams && Object.keys(initialParams).length > 0) {
			log('[Analytics] Processing initial route params:', initialParams);
			this.initialParamsProcessed = true;
			this.processRouteChange(initialParams);
		}

		// Listen for future route changes
		this.router.events
			.pipe(
				takeUntilDestroyed(),
				filter(
					e =>
						e instanceof ActivationEnd &&
						Object.keys(e.snapshot.params).length > 0
				),
				map(e => (e instanceof ActivationEnd ? e.snapshot.params : {}))
			)
			.subscribe(params => {
				// Skip duplicate processing for same route params
				if (
					this.initialParamsProcessed &&
					this.isSameRouteParams(params)
				) {
					return;
				}
				this.processRouteChange(params);
			});
	}

	private isSameRouteParams(params: Record<string, string>): boolean {
		const incomingReport = params.reportId?.toLowerCase() ?? null;
		const storedReportId = this.reportId();
		const currentReport =
			storedReportId != null ? storedReportId.toLowerCase() : null;
		return (
			incomingReport === currentReport &&
			this.isSamePage(params.pageId || '', this.pageId() || '')
		);
	}

	/**
	 * Process route change with proper sequencing to avoid race conditions.
	 *
	 * CRITICAL: The order of operations matters here:
	 * 1. Parse route params first (sets accountId, viewId, pageId)
	 * 2. Start WebSocket subscription and WAIT for connection
	 * 3. Only then load view config (which triggers GraphQL requests that expect WebSocket responses)
	 * 4. Load themes (independent, can run in parallel)
	 *
	 * This prevents the race condition where GraphQL triggers backend processing
	 * before the WebSocket is ready to receive the results.
	 */
	private async processRouteChange(
		params: Record<string, string>
	): Promise<void> {
		this.handleRouteParams(params);
		this.adoptEmbedViewerToken();

		if ((await this.resolveShareAccess()) !== 'allowed') {
			return;
		}

		// Start WebSocket subscription first - this sets up the listener
		this.startAppSyncSubscription();

		// Wait for WebSocket to be connected before triggering GraphQL requests
		// that will cause the backend to send data via WebSocket
		try {
			await this.syncService.waitForConnection();
			log('[Analytics] WebSocket connected, proceeding with data load');
		} catch (err) {
			reportAnalyticsError(
				'[Analytics] WebSocket connection failed, continuing anyway:',
				err,
				{ feature: 'analytics', operation: 'websocketConnect' }
			);
		}

		// Now safe to load config - WebSocket is ready to receive data
		await this.loadViewConfig();
		await this.ensureViewThemeApplied();
	}

	private async resolveShareAccess(): Promise<ReportAccessState> {
		const reportId = this.reportId();
		if (!reportId) {
			this.#clearGateBranding();
			this.accessState.set('allowed');
			return 'allowed';
		}

		this.requestService.setActiveReport(reportId);
		this.accessState.set('checking');

		const lookup = await this.viewAccessService.getViewAccess(reportId);
		this.#syncGateBranding(lookup);

		if (this.isShareGatePreviewForced()) {
			this.accessState.set('gate');
			return 'gate';
		}

		if (lookup.status === 'unavailable') {
			log('[Analytics] getViewAccess unavailable — allowing guest load', {
				reportId,
			});
			this.accessState.set('allowed');
			return 'allowed';
		}

		const access = lookup.access;
		log('[Analytics] getViewAccess resolved', {
			reportId,
			accessMode: access.accessMode,
			status: access.status,
			clientName: access.client?.name ?? null,
		});

		// The embed owner token opens the owner's own private and hidden shares; the
		// AppSync authorizer decides what it may see. Email-code sessions stay behind
		// the share's status and expiry below.
		if (
			this.#embedOwnerTokenReportId === reportId &&
			this.viewerAuth.hasValidJwtForReport(reportId)
		) {
			this.accessState.set('allowed');
			return 'allowed';
		}

		if (!isViewAccessActive(access)) {
			this.accessState.set('denied');
			return 'denied';
		}

		if (
			access.accessMode === 'PRIVATE' &&
			!this.viewerAuth.hasValidJwtForReport(reportId) &&
			!this.captureMode
		) {
			this.accessState.set('gate');
			return 'gate';
		}

		this.accessState.set('allowed');
		return 'allowed';
	}

	/** Host-supplied owner token (`window.__JEPTO_EMBED__`) for the current report. */
	private adoptEmbedViewerToken(): void {
		this.#embedOwnerTokenReportId = null;
		const reportId = this.reportId();
		if (!reportId) {
			return;
		}
		const token = readEmbedViewerToken(reportId);
		if (token) {
			this.viewerAuth.storeViewerSession('', reportId, token);
			this.#embedOwnerTokenReportId = reportId;
		}
	}

	/** Dev-only: `?gatePreview=1` forces the share gate UI without changing ACL. */
	private isShareGatePreviewForced(): boolean {
		if (!environment.develop || typeof window === 'undefined') {
			return false;
		}
		return new URLSearchParams(window.location.search).has('gatePreview');
	}

	#syncGateBranding(lookup: ViewAccessLookup): void {
		if (lookup.status === 'ok') {
			this.#applyGateBranding(lookup.access);
			return;
		}
		this.#clearGateBranding();
	}

	#applyGateBranding(access: ViewAccessPublic): void {
		const { name, logo } = clientBrandingFromViewAccess(access);
		this.gateBrandName.set(name);
		this.gateBrandLogoUrl.set(logo);
	}

	#clearGateBranding(): void {
		this.gateBrandName.set(undefined);
		this.gateBrandLogoUrl.set(undefined);
	}

	async onAccessVerified(): Promise<void> {
		const reportId = this.reportId();
		if (!reportId) {
			return;
		}
		this.requestService.setActiveReport(reportId);
		this.accessState.set('allowed');

		this.startAppSyncSubscription();
		try {
			await this.syncService.waitForConnection();
		} catch (err) {
			reportAnalyticsError(
				'[Analytics] WebSocket connection failed after verification:',
				err,
				{ feature: 'analytics', operation: 'websocketConnect' }
			);
		}

		await this.loadViewConfig();
		await this.ensureViewThemeApplied();
	}

	/**
	 * Load view config using async/await.
	 */
	private async loadViewConfig(): Promise<void> {
		const aid = this.accountId();
		const vid = this.viewId();
		const pid = this.pageId();
		// Root share URL `/{reportId}` has no page segment — same as main app
		// `ReportEditorStore.loadReport` (`pageId: null` on AnalyticsView).
		const pageIdForQuery =
			pid != null && String(pid).trim().length > 0 ? pid : null;

		if (!aid || !vid) return;

		try {
			log(
				`[Analytics] Getting view config (pageId: ${pageIdForQuery ?? 'null'})`
			);
			const config = await this.analyticsService.fetchViewConfig(
				aid,
				vid,
				pageIdForQuery
			);
			log('[Analytics] View config loaded', config);
			this.handleViewConfig(config);
			this.trackReportView();
		} catch (error) {
			if (this.#shouldReturnToShareGate(error)) {
				const reportId = this.reportId();
				if (reportId) {
					this.viewerAuth.clearJwtForReport(reportId);
					this.#syncGateBranding(
						await this.viewAccessService.getViewAccess(reportId)
					);
				}
				this.viewConfig.set(null);
				this.accessState.set('gate');
				return;
			}
			reportAnalyticsError(
				'[Analytics] Failed to load view config:',
				error,
				{ feature: 'analytics', operation: 'loadViewConfig' }
			);
		}
	}

	#shouldReturnToShareGate(error: unknown): boolean {
		if (this.captureMode) {
			return false;
		}
		return this.#isViewerUnauthorizedError(error);
	}

	#isViewerUnauthorizedError(error: unknown): boolean {
		if (!(error instanceof Error)) {
			return false;
		}
		if (error.message === 'AppSyncViewerUnauthorized') {
			return true;
		}
		return /access denied/i.test(error.message);
	}

	private trackReportView(): void {
		const reportId = this.reportId();
		if (!reportId) {
			return;
		}

		this.reportViewTracking.trackView({
			reportId,
			deliveryId: parseDeliveryIdFromUrl(),
		});
	}

	private handleRouteParams(params: Record<string, string>): void {
		log('[Analytics] Route params received:', params);

		const queryReportId =
			typeof window !== 'undefined'
				? new URLSearchParams(window.location.search).get('reportId')
				: null;
		const resolved = resolveEncodedReportIds(
			params.reportId,
			queryReportId
		);

		this.reportId.set(resolved?.reportId ?? null);
		this.accountId.set(resolved?.accountId ?? null);
		this.viewId.set(resolved?.viewId ?? null);

		this.rawPageFromUrl = params.pageId;
		this.pageId.set(this.ensurePagePrefix(params.pageId));

		log('[Analytics] Parsed route params:', {
			reportId: this.reportId(),
			accountId: this.accountId(),
			viewId: this.viewId(),
			rawPageId: params.pageId,
			pageId: this.pageId(),
			rawPageFromUrl: this.rawPageFromUrl,
			currentUrl: window.location.href,
		});
	}

	private handleViewConfig(config: ViewConfig): void {
		this.captureReadiness.resetForView();
		this.viewConfig.set(config);
		this.titleService.setTitle(`${config.title}: ${config.description}`);
		this.cardService.clearAllCards();
		this.dataService.clearAllData();
		this.pageFilterService.clear();
		this.activePageDateRangeExpression.set(undefined);

		// Register cards with basic metadata; full configs loaded via getCardConfigs
		const allCards = this.extractAllCards(config);
		log(
			`[Analytics] Registering ${allCards.length} cards from view config`
		);
		allCards.forEach(card => {
			this.cardService.setCardConfig(card.cardId, card as CardConfig);
		});

		this.handlePalettes([]);
		this.initializePageFromViewConfig();
	}

	// TODO(fallow): reduce complexity — deferred; revisit after remove-getCard-on-ws-config
	// fallow-ignore-next-line complexity
	private async ensureViewThemeApplied(): Promise<void> {
		const config = this.viewConfig();
		if (!config) {
			return;
		}

		try {
			await this.#hydrateViewTheme(config);
		} finally {
			this.captureReadiness.markViewReady();
		}
	}

	#applyViewThemeConfig(theme: ThemeConfig, themeId: string): void {
		this.themeService.mergeAvailableThemes([theme]);
		this.themeService.selectThemeById(themeId);
		this.themeService.loadThemeFromConfig(theme);
	}

	async #hydrateViewTheme(config: ViewConfig): Promise<void> {
		const themeId = config.themeId?.trim();
		if (!themeId) {
			if (config.mode) {
				this.themeService.prepareForViewConfigInit();
				this.themeService.initFromViewConfig(config.mode);
			}
			return;
		}

		if (isBuiltInThemeId(themeId)) {
			this.#applyViewThemeConfig(DEFAULT_THEME, themeId);
		} else {
			// Paint the getView embed first so :root is not default while getTheme
			// is in flight. Hydrate from getTheme afterward (sparse embeds map to
			// DEFAULT_THEME colors and would otherwise stick).
			let theme: ThemeConfig | null = config.theme ?? null;
			if (theme) {
				this.#applyViewThemeConfig(theme, themeId);
				if (config.mode) {
					this.themeService.prepareForViewConfigInit();
					this.themeService.initFromViewConfig(config.mode);
				}
			}
			const aid = this.accountId();
			if (aid) {
				try {
					const fetched = await this.analyticsService.fetchTheme(
						aid,
						themeId
					);
					if (fetched) {
						theme = fetched;
					}
				} catch (error) {
					reportAnalyticsError(
						'[Analytics] Failed to load view theme:',
						error,
						{
							feature: 'analytics',
							operation: 'fetchTheme',
							themeId,
						}
					);
				}
			}
			theme ??= this.themeService.getThemeById(themeId);
			if (theme) {
				this.#applyViewThemeConfig(theme, themeId);
			} else {
				warn(
					`[Analytics] View theme "${themeId}" could not be resolved. ` +
						'Falling back to built-in default.'
				);
				this.#applyViewThemeConfig(
					DEFAULT_THEME,
					DEFAULT_THEME.themeId
				);
			}
		}

		if (config.mode) {
			this.themeService.prepareForViewConfigInit();
			this.themeService.initFromViewConfig(config.mode);
		}
	}

	// TODO(fallow): reduce complexity — deferred; revisit after remove-getCard-on-ws-config
	// fallow-ignore-next-line complexity
	private initializePageFromViewConfig(): void {
		const currentViewConfig = this.viewConfig();
		const currentPageId = this.pageId();

		log('[Analytics] initializePageFromViewConfig called with:', {
			hasViewConfig: !!currentViewConfig,
			pagesCount: currentViewConfig?.pages?.length || 0,
			pageId: currentPageId,
			rawPageFromUrl: this.rawPageFromUrl,
			currentUrl: window.location.href,
		});

		if (
			!currentViewConfig ||
			!currentViewConfig.pages ||
			currentViewConfig.pages.length === 0
		) {
			log('[Analytics] No view config or pages available, returning');
			return;
		}

		let targetPage: PageConfig | undefined;

		// If we have a page ID from the URL, use that exclusively
		if (currentPageId) {
			log('[Analytics] Looking for page with ID:', currentPageId);

			targetPage = this.findPageByAnyIdFormat(currentPageId);
			log(
				`[Analytics] Found page in view config: ${
					targetPage?.pageId || 'not found'
				}`
			);

			// If we didn't find the page by ID, try the raw URL
			if (!targetPage && this.rawPageFromUrl) {
				log(
					'[Analytics] Trying to find page by raw URL:',
					this.rawPageFromUrl
				);
				targetPage = currentViewConfig.pages.find(
					p => this.normalizePageId(p.pageId) === this.rawPageFromUrl
				);
				log(
					'[Analytics] Found page by raw URL:',
					targetPage?.pageId || 'not found'
				);
			}

			// If still not found, request the page config directly instead of defaulting to page 1
			if (!targetPage) {
				log(
					`[Analytics] Page ${currentPageId} not found in view config, requesting directly`
				);
				this.loadPageConfig();
				return;
			}
		}

		// Only default to first page if no page ID was specified
		else if (currentViewConfig.pages.length > 0) {
			targetPage = currentViewConfig.pages[0];
			log(
				`[Analytics] No page ID specified, defaulting to first page: ${targetPage.pageId}`
			);
		}

		if (targetPage) {
			log(`[Analytics] Target page found: ${targetPage.pageId}`);

			this.pageId.set(targetPage.pageId);
			// Scope page-filter stash to the initial page before any selection.
			this.pageFilterService.activatePage(targetPage.pageId, {
				emitRefresh: false,
			});
			const pageIdForUrl = this.normalizePageId(this.pageId()!);

			// Only change URL if no page ID was originally specified (defaulting to first page)
			if (!this.rawPageFromUrl && this.reportId()) {
				const newPath = `/${this.reportId()}/page/${pageIdForUrl}`;
				const queryString = window.location.search; // Preserve query parameters
				const newUrl = queryString
					? `${newPath}${queryString}`
					: newPath;
				log('[Analytics] Changing URL to:', newUrl);
				this.location.go(newUrl);
			}

			// Process the current page (layouts and cards)
			this.loadPage(targetPage);

			// Note: View component will automatically select the page via its selectedPage input binding
			// No need to manually call selectPageById - Angular's change detection handles this
		}
	}

	// TODO(fallow): reduce complexity — deferred; revisit after remove-getCard-on-ws-config
	// fallow-ignore-next-line complexity
	private handleSelectedPage(newPageId: string): void {
		const currentPageId = this.pageId();
		const currentViewConfig = this.viewConfig();

		log('[Analytics] handleSelectedPage called with:', {
			pageId: newPageId,
			currentPageId: currentPageId,
			hasViewConfig: !!currentViewConfig,
			currentUrl: window.location.href,
		});

		if (!newPageId || !currentViewConfig) {
			log('[Analytics] handleSelectedPage: Missing pageId or viewConfig');
			return;
		}

		if (currentPageId && this.isSamePage(newPageId, currentPageId)) {
			log('[Analytics] handleSelectedPage: Same page, returning');
			return;
		}

		const page = this.findPageByAnyIdFormat(newPageId);

		if (!page) {
			reportAnalyticsError(
				`[Analytics] Page not found for ID: ${newPageId}`,
				new Error(`Page not found: ${newPageId}`),
				{ feature: 'analytics', operation: 'pageNotFound' }
			);
			return;
		}

		log('[Analytics] handleSelectedPage: Found page, changing to:', {
			fromPageId: currentPageId,
			toPageId: page.pageId,
			normalizedPageId: this.normalizePageId(page.pageId),
		});

		this.pageLoading.set(true);
		this.pageId.set(page.pageId);
		// Keep per-page dropdown selections across tabs; refetch with restored
		// pageFilters so charts match the still-visible filter UI (JEP-10140).
		this.pageFilterService.activatePage(page.pageId);
		this.activePageDateRangeExpression.set(undefined);

		const pageIdForUrl = this.normalizePageId(page.pageId);

		if (this.reportId()) {
			const newPath = `/${this.reportId()}/page/${pageIdForUrl}`;
			const queryString = window.location.search; // Preserve query parameters
			const newUrl = queryString ? `${newPath}${queryString}` : newPath;
			log('[Analytics] handleSelectedPage: Updating URL to:', newUrl);
			this.location.go(newUrl);
		}

		try {
			const viewComp = this.viewComponent();
			if (viewComp && !viewComp.isPageCached(page.pageId)) {
				this.layoutService.clearLayouts();
			}

			this.loadPage(page);
		} catch (error) {
			reportAnalyticsError('[Analytics] Error processing page:', error, {
				feature: 'analytics',
				operation: 'processPage',
			});
			this.pageLoading.set(false);
		}
	}

	private findPageByAnyIdFormat(id: string): PageConfig | undefined {
		const config = this.viewConfig();
		if (!id || !config?.pages) return undefined;

		const normalizedId = this.normalizePageId(id);
		return config.pages.find(
			p => this.normalizePageId(p.pageId) === normalizedId
		);
	}

	private loadPage(page: PageConfig): void {
		this.pageService.setPageConfig(page);

		const pageCards = requirePageLayout(page);
		if (pageCards.length > 0) {
			const layout: LayoutConfig = {
				layoutId: deriveLayoutIdForPage(page.pageId),
				pageId: page.pageId,
				order: page.order ?? 1,
				cards: pageCards,
			};

			this.layoutService.setLayoutConfig(layout);
			const cardsToLoad = this.getCardsNeedingConfigLoad(pageCards);
			const currentView = this.viewConfig();
			if (cardsToLoad.length > 0 && currentView) {
				log(
					`[Analytics] Loading ${cardsToLoad.length} cards that need config`
				);
				this.getCardConfigs(
					currentView.accountId,
					page.pageId,
					cardsToLoad
				);
			}
			this.pageLoading.set(false);
			this.captureReadiness.beginPage(pageCards);
			return;
		}

		this.loadPageConfig();
	}

	private getCardsNeedingConfigLoad(cards: CardList[]): CardList[] {
		return cards.filter(card => {
			const existingCard = this.cardService.cardConfig(card.cardId)();
			const hasData =
				(existingCard?.cardType === 'chart' &&
					!!(existingCard as ChartCardConfig | undefined)?.data) ||
				(existingCard?.cardType === 'table' &&
					this.tableCardHasLoadedData(card.cardId));
			return !hasData;
		});
	}

	private extractAllCards(viewConfig: ViewConfig): CardList[] {
		return (
			viewConfig?.pages?.flatMap(page => requirePageLayout(page)) ?? []
		);
	}

	private startAppSyncSubscription(): void {
		if (this.appSyncSubscriptionStarted) {
			return;
		}

		const aid = this.accountId();
		const vid = this.viewId();

		if (!aid || !vid) {
			log(
				'[Analytics] Cannot start AppSync subscription - missing accountId or viewId'
			);
			return;
		}

		const currentIdentityId = this.identityId();
		if (!currentIdentityId) {
			log(
				'[Analytics] Starting AppSync subscription without identity ID - events will be buffered until ID is available'
			);
		}

		log('[Analytics] Starting AppSync subscription', {
			accountId: aid,
			viewId: vid,
			identityId: currentIdentityId || '(pending)',
		});

		this.appSyncSubscriptionStarted = true;

		this.analyticsService
			.subscribeToViewData(aid, vid)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((syncEvent: SyncEvent) => {
				// Handle different event types
				if (syncEvent.type === 'connected') {
					log('[Analytics] WebSocket subscription connected');
					// Process any buffered events now that we're connected
					this.processPendingEvents();
					return;
				}

				if (
					syncEvent.type === 'error' ||
					syncEvent.type === 'disconnected'
				) {
					warn(
						'[Analytics] WebSocket event:',
						syncEvent.type,
						syncEvent.errorMessage
					);
					return;
				}

				// Handle data events
				if (syncEvent.type === 'data') {
					const parsed = parseAppSyncCardEvent(syncEvent.data.event);
					if (parsed) {
						this.processWebSocketEvent(
							parsed as unknown as WebSocketEvent
						);
					}
				}
			});
	}

	/**
	 * Process a WebSocket event, buffering if identity is not yet available.
	 * This prevents the race condition where events arrive before identity ID is set.
	 */
	private processWebSocketEvent(event: WebSocketEvent): void {
		if (event.messageType === 'config') {
			this.handleCardConfig(event);
			return;
		}

		const incomingRequester = webSocketRequesterFromEvent(event);
		const requesterDecision =
			this.analyticsDataRequester.evaluateWebSocketRequester(
				incomingRequester
			);
		if (requesterDecision === 'pending') {
			log(
				'[Analytics] Buffering WebSocket event until requester is available:',
				event.cardId
			);
			this.pendingWebSocketEvents.push(event);
			return;
		}
		if (requesterDecision === 'reject') {
			log('[Analytics] Ignoring WebSocket event for foreign requester:', {
				cardId: event.cardId,
				requestId: event.requestId,
				requester: incomingRequester,
			});
			return;
		}

		const myIdentityId = this.identityId();

		// Session-scoped events need identity before we can filter; Jepto-style
		// payloads use `identityId: null` and should apply immediately.
		if (!myIdentityId) {
			if (event.identityId == null) {
				this.handleCardData(event);
				return;
			}
			log(
				'[Analytics] Buffering WebSocket event until identity is available:',
				event.cardId
			);
			this.pendingWebSocketEvents.push(event);
			return;
		}

		// Filter by identityId only when the event is not already requester-scoped.
		// Public getCardData pagination has sent matching `requester` with a drifted
		// `identityId`; requester gate already accepted those — do not drop them.
		// `identityId: null` is used for authenticated Jepto hosts (user-pool).
		if (
			!incomingRequester &&
			event.identityId != null &&
			event.identityId !== myIdentityId
		) {
			log(
				'[Analytics] Ignoring WebSocket event for foreign identityId:',
				{
					cardId: event.cardId,
					requestId: event.requestId,
					identityId: event.identityId,
					myIdentityId,
				}
			);
			return;
		}

		// All WebSocket messages deliver data (content for cards)
		// Route based on cardType to the appropriate handler
		this.handleCardData(event);
	}

	/**
	 * Process any buffered WebSocket events once identity is available.
	 */
	private processPendingEvents(): void {
		if (this.pendingWebSocketEvents.length === 0) {
			return;
		}

		if (!this.analyticsDataRequester.isRequesterReady()) {
			log(
				'[Analytics] Cannot process pending events - requester still not available'
			);
			return;
		}

		log(
			`[Analytics] Processing ${this.pendingWebSocketEvents.length} buffered WebSocket events`
		);

		const events = [...this.pendingWebSocketEvents];
		this.pendingWebSocketEvents = [];

		events.forEach(event => this.processWebSocketEvent(event));
	}

	/**
	 * AppSync `messageType: 'config'` for cards. Same apply policy as Jepto
	 * reports: hydrate via getCard, do not merge the socket body as CardConfig.
	 */
	private handleCardConfig(event: ConfigWebSocketEvent): void {
		void this.#applyCardConfigFromWebSocket(
			event as unknown as Record<string, unknown>
		);
	}

	async #applyCardConfigFromWebSocket(
		event: Record<string, unknown>
	): Promise<void> {
		const cardId =
			typeof event['cardId'] === 'string' ? event['cardId'].trim() : '';
		const existing = cardId
			? this.cardService.cardConfig(cardId)()
			: undefined;
		const owningPageId = cardId
			? resolveCardLayoutPageId(
					this.layoutState.getAllLayouts().values(),
					this.pageState.allPages(),
					cardId
				) ||
				existing?.pageId?.trim() ||
				null
			: null;
		const decision = decideCardConfigRealtimeApply(event, {
			currentActorIds: [this.analyticsDataRequester.getCachedRequester()],
			localRevisions:
				typeof existing?.revisions === 'number'
					? existing.revisions
					: undefined,
			hasPendingChanges: false,
			cardInState: !!existing,
			cardOnLayout: !!owningPageId,
		});

		log('[Analytics][WS][Card][config]', {
			cardId,
			decision,
			eventAction: event['eventAction'],
			entityType: event['entityType'],
		});

		if (shouldApplyCardConfigRealtimeContent(decision, event)) {
			this.dataService.updateContentData(
				cardId,
				event['content'] as string,
				typeof event['requestId'] === 'string'
					? event['requestId']
					: undefined
			);
		}

		if (decision.action === 'ignore') {
			return;
		}

		if (decision.action === 'delete') {
			if (cardId) {
				this.#bumpCardConfigRealtimeGeneration(cardId);
				this.#removeCardFromRealtimeLayouts(cardId);
				this.cardService.deleteCard(cardId);
			}
			return;
		}

		const generation = this.#bumpCardConfigRealtimeGeneration(cardId);
		const cardType =
			typeof event['cardType'] === 'string'
				? event['cardType'].trim().toLowerCase()
				: '';
		const aid = this.accountId();
		const pageIdForCardQuery = owningPageId || this.pageId();
		if (!aid || !pageIdForCardQuery) {
			return;
		}
		await this.getCardConfigs(
			aid,
			pageIdForCardQuery,
			[
				{
					cardId,
					cardType: (cardType || CardType.Chart) as CardType,
					x: 0,
					y: 0,
					w: 1,
					h: 1,
				},
			],
			{ cardId, generation }
		);
	}

	#bumpCardConfigRealtimeGeneration(cardId: string): number {
		const next = (this.#cardConfigRealtimeGeneration.get(cardId) ?? 0) + 1;
		this.#cardConfigRealtimeGeneration.set(cardId, next);
		return next;
	}

	#removeCardFromRealtimeLayouts(cardId: string): void {
		const trimmedCardId = cardId.trim();
		if (!trimmedCardId) {
			return;
		}

		for (const layout of [...this.layoutState.getAllLayouts().values()]) {
			if (!layout.cards?.some(card => card.cardId === trimmedCardId)) {
				continue;
			}
			const newCards = layout.cards.filter(
				card => card.cardId !== trimmedCardId
			);
			this.layoutState.updateLayout(layout.layoutId, { cards: newCards });
			const pageId = layout.pageId?.trim();
			if (pageId && this.pageState.getPage(pageId)) {
				this.pageState.updatePage(pageId, { layout: newCards });
			}
		}

		for (const page of this.pageState.allPages()) {
			const pageLayout = page.layout ?? [];
			if (!pageLayout.some(card => card.cardId === trimmedCardId)) {
				continue;
			}
			this.pageState.updatePage(page.pageId, {
				layout: pageLayout.filter(
					card => card.cardId !== trimmedCardId
				),
			});
		}
	}

	/**
	 * Handle incoming WebSocket data for any card type.
	 * Routes data to DataService which manages all card data reactively.
	 *
	 * WebSocket delivers DATA (content/series/filters/image-refs).
	 * Card CONFIG (metadata like title, style) comes from GraphQL.
	 */
	private handleCardData(event: DataWebSocketEvent): void | Promise<void> {
		const hasDataRef = Boolean(event.dataRef?.trim());
		if (hasDataRef) {
			log('[Analytics][dataRef] handleCardData: received', {
				cardId: event.cardId,
				cardType: event.cardType,
				requestId: event.requestId,
				resolveDataRef: shouldResolveCardDataRef(event),
			});
		}
		if (!this.#acceptsCardDataEvent(event)) {
			if (hasDataRef) {
				log(
					'[Analytics][dataRef] handleCardData: stopped at gate (pre-fetch)'
				);
			}
			return;
		}
		if (shouldResolveCardDataRef(event)) {
			return this.#applyResolvedCardDataRef(event);
		}
		if (hasDataRef) {
			log(
				'[Analytics][dataRef] handleCardData: skipped fetch — cardType not chart/table/analysis',
				{ cardId: event.cardId, cardType: event.cardType }
			);
		}
		this.#dispatchCardData(event);
	}

	#acceptsCardDataEvent(event: DataWebSocketEvent): boolean {
		const requesterDecision =
			this.analyticsDataRequester.evaluateWebSocketRequester(
				webSocketRequesterFromEvent(event)
			);
		if (requesterDecision !== 'accept') {
			log(
				'[Analytics] Ignoring WebSocket card data for requester gate:',
				{
					cardId: event.cardId,
					requestId: event.requestId,
					requester: webSocketRequesterFromEvent(event),
					decision: requesterDecision,
				}
			);
			return false;
		}

		const pageIdForOrdering =
			'pageId' in event && typeof event.pageId === 'string'
				? event.pageId
				: (this.pageId() ?? undefined);

		// Filter options and analysis periods use getCard/getCardData requestIds,
		// not getPageData batches. Same gate skip as clients-report — page floor
		// would drop those WS events when getPageData races after selection change.
		const skipPageRefreshFloor =
			PageDataRefreshCoordinatorService.skipsPageDataRefreshFloor(
				event.cardType
			);
		if (
			!skipPageRefreshFloor &&
			!this.pageDataRefreshCoordinator.shouldAcceptWebSocketUpdate(
				pageIdForOrdering,
				event.requestId
			)
		) {
			log('[Analytics] Ignoring stale page refresh WebSocket update:', {
				cardId: event.cardId,
				requestId: event.requestId,
				pageId: pageIdForOrdering,
				floor: this.pageDataRefreshCoordinator.getFloorForPage(
					pageIdForOrdering
				),
				hasDataRef: Boolean(event.dataRef?.trim()),
			});
			return false;
		}
		return true;
	}

	async #applyResolvedCardDataRef(event: DataWebSocketEvent): Promise<void> {
		const dataRef = event.dataRef?.trim() ?? '';
		log('[Analytics][dataRef] applyResolvedCardDataRef: fetching', {
			cardId: event.cardId,
			requestId: event.requestId,
			dataRef,
		});
		const result = await this.cardDataRef.fetchPayload(dataRef);
		if (result.ok === false) {
			error('[Analytics] Card dataRef fetch failed', {
				cardId: event.cardId,
				requestId: event.requestId,
				dataRef,
				message: cardDataRefErrorMessage(result),
			});
			log(
				'[Analytics][dataRef] applyResolvedCardDataRef: stopped at fetch failure'
			);
			this.cardRuntimeRefreshState.endCardRefresh(event.cardId);
			return;
		}
		if (!this.#acceptsCardDataEvent(event)) {
			log(
				'[Analytics][dataRef] applyResolvedCardDataRef: stopped at gate (post-fetch)',
				{
					cardId: event.cardId,
					requestId: event.requestId,
					pageId:
						'pageId' in event && typeof event.pageId === 'string'
							? event.pageId
							: (this.pageId() ?? undefined),
					floor: this.pageDataRefreshCoordinator.getFloorForPage(
						'pageId' in event && typeof event.pageId === 'string'
							? event.pageId
							: (this.pageId() ?? undefined)
					),
				}
			);
			return;
		}
		log('[Analytics][dataRef] applyResolvedCardDataRef: dispatching', {
			cardId: event.cardId,
			requestId: event.requestId,
			cardType: event.cardType,
		});
		this.#dispatchCardData({
			...event,
			data: result.data,
		} as DataWebSocketEvent);
	}

	// fallow-ignore-next-line complexity
	#dispatchCardData(event: DataWebSocketEvent): void {
		const pageIdForOrdering =
			'pageId' in event && typeof event.pageId === 'string'
				? event.pageId
				: (this.pageId() ?? undefined);

		if (event.requestId) {
			this.analyticsDataLatency.completeFromWebSocket({
				requestId: event.requestId,
				cardId: event.cardId,
				pageId: pageIdForOrdering,
				executionId:
					'executionId' in event &&
					typeof event.executionId === 'string'
						? event.executionId
						: undefined,
				cardType: event.cardType,
				dataSourceServices: dataSourceServicesFromCardConfig(
					this.cardService.cardConfig(event.cardId)()
				),
			});
		}

		log('[Analytics] WebSocket event received:', {
			cardId: event.cardId,
			cardType: event.cardType,
			requestId: event.requestId,
		});

		switch (event.cardType) {
			case 'chart': {
				const cacheStatus = readWebSocketCacheStatus(event);
				const applied = applyChartWebSocketDataToDataService(
					this.dataService,
					{
						cardId: event.cardId,
						chartType: event.chartType,
						data: event.data,
						requestId: event.requestId,
					}
				);
				if (!applied) {
					warn(
						'[Analytics] WebSocket chart event has no plottable series:',
						event.cardId
					);
					if (event.dataRef?.trim()) {
						log(
							'[Analytics][dataRef] dispatchCardData: stopped — chart payload not plottable',
							{ cardId: event.cardId, requestId: event.requestId }
						);
					}
				} else if (event.dataRef?.trim()) {
					log(
						'[Analytics][dataRef] dispatchCardData: chart applied',
						{
							cardId: event.cardId,
							requestId: event.requestId,
						}
					);
				}
				if (
					shouldEndCardRefreshAfterWebSocketChartEvent(
						cacheStatus,
						applied
					)
				) {
					this.cardRuntimeRefreshState.endCardRefresh(event.cardId);
				}
				break;
			}

			case 'table': {
				const applied = applyTableWebSocketDataToDataService(
					this.dataService,
					{
						cardId: event.cardId,
						data: event.data,
						requestId: event.requestId,
						executionId: (event as { executionId?: string })
							.executionId,
					}
				);
				if (!applied) {
					warn(
						'[Analytics] WebSocket table event has no renderable columns:',
						event.cardId
					);
				}
				if (
					shouldEndCardRefreshAfterWebSocket(
						readWebSocketCacheStatus(event)
					)
				) {
					this.cardRuntimeRefreshState.endCardRefresh(event.cardId);
				}
				break;
			}

			case 'html':
			case 'markdown':
				if (event.content !== undefined) {
					this.dataService.updateContentData(
						event.cardId,
						event.content,
						event.requestId
					);
				} else {
					warn(
						'[Analytics] WebSocket content event has no content:',
						event.cardId
					);
				}
				break;

			case 'filter':
				if (event.data !== undefined) {
					this.dataService.updateFilterData(
						event.cardId,
						event.data,
						event.requestId
					);
				} else {
					warn(
						'[Analytics] WebSocket filter event has no data:',
						event.cardId
					);
				}
				break;

			case 'image':
				this.dataService.updateImageData(
					event.cardId,
					{
						imageRef: event.imageRef,
						imageRefLight: event.imageRefLight,
						imageRefDark: event.imageRefDark,
					},
					event.requestId
				);
				break;

			case 'analysis': {
				if (
					!applyAnalysesDataToDataService(this.dataService, {
						cardId: event.cardId,
						data: event.data,
						requestId: event.requestId,
					})
				) {
					warn(
						'[Analytics][WS][analysis] ignored event without analyses',
						{
							cardId: event.cardId,
							requestId: event.requestId,
						}
					);
				}
				break;
			}

			default: {
				const _exhaustive: never = event;
				warn('[Analytics] Unhandled WebSocket cardType:', _exhaustive);
			}
		}

		this.#settleCaptureAfterWebSocket(event);
	}

	#settleCaptureAfterWebSocket(event: DataWebSocketEvent): void {
		const layoutCard: CardList = {
			cardId: event.cardId,
			cardType: event.cardType as CardType,
			x: 0,
			y: 0,
			w: 0,
			h: 0,
		};
		if (
			shouldSettleCaptureAfterWebSocket(
				event.cardType,
				layoutCard,
				this.cardService.cardConfig(event.cardId)(),
				this.dataService
			)
		) {
			this.captureReadiness.markCardSettled(event.cardId);
		}
	}

	/**
	 * Fetch card configs in parallel via {@link Promise.allSettled} so one failure does not abort the batch.
	 * `pageId` may be `null` for report-root URLs until the active page is resolved; the resolver matches the main app.
	 */
	private async getCardConfigs(
		accountId: string,
		pageIdForCardQuery: string | null,
		cardConfigs: CardList[],
		realtimeApply?: { cardId: string; generation: number }
	): Promise<void> {
		const vid = this.viewId();
		if (!vid || cardConfigs.length === 0) return;

		log(
			`[Analytics] Fetching configs for ${cardConfigs.length} cards (pageId=${pageIdForCardQuery ?? 'null'})`
		);

		try {
			const results = await Promise.allSettled(
				cardConfigs.map(card =>
					this.analyticsService.fetchCardConfig(
						card.cardId,
						vid,
						accountId,
						pageIdForCardQuery
					)
				)
			);

			const validConfigs: Partial<CardConfig>[] = [];
			results.forEach((result, index) => {
				if (result.status === 'fulfilled') {
					validConfigs.push(result.value);
					return;
				}
				const cardId = cardConfigs[index]?.cardId ?? '?';
				reportAnalyticsError(
					`[Analytics] getCard failed for cardId=${cardId} accountId=${accountId} viewId=${vid} pageId=${pageIdForCardQuery ?? 'null'}:`,
					result.reason,
					{ feature: 'analytics', operation: 'getCard' }
				);
			});

			log(
				`[Analytics] Received ${validConfigs.length} card configs from API`
			);

			this.loadDropdownFilterCardData(cardConfigs);

			validConfigs.forEach((config: Partial<CardConfig>) => {
				if (
					realtimeApply &&
					config.cardId === realtimeApply.cardId &&
					this.#cardConfigRealtimeGeneration.get(config.cardId) !==
						realtimeApply.generation
				) {
					return;
				}
				// Get existing card to check if it has AppSync data we should preserve
				const existingCard = this.cardService.cardConfig(
					config.cardId
				)();

				// If the existing card has chart data from AppSync, preserve it
				// The API returns config metadata, not the actual chart series data
				if (existingCard && (existingCard as ChartCardConfig).data) {
					const { data: incomingData, ...configWithoutData } =
						config as Partial<ChartCardConfig> & {
							data?: Record<string, unknown>;
						};
					const mergedData = mergeCardDataFiltersFromApi(
						(existingCard as ChartCardConfig).data as
							| Record<string, unknown>
							| undefined,
						incomingData
					);
					this.cardService.updateCardConfig(
						config.cardId,
						mergedData
							? ({
									...configWithoutData,
									data: mergedData,
								} as Partial<ChartCardConfig>)
							: (configWithoutData as Partial<ChartCardConfig>)
					);
				} else {
					// No existing data, safe to apply full config
					this.cardService.updateCardConfig(
						config.cardId,
						config as Partial<ChartCardConfig>
					);
				}
			});
		} catch (error) {
			reportAnalyticsError(
				'[Analytics] Error fetching card configs:',
				error,
				{ feature: 'analytics', operation: 'fetchCardConfigs' }
			);
		}
	}

	/**
	 * Fetch card data using async/await.
	 */
	private async getCardData(
		cardId: string,
		dateRange: string[]
	): Promise<void> {
		const aid = this.accountId();
		if (!aid) return;

		try {
			const data = await this.analyticsService.fetchCardData(
				cardId,
				{ dateRange },
				aid
			);
			log('[Analytics] Card data loaded', data);
		} catch (error) {
			reportAnalyticsError(
				'[Analytics] Error fetching card data:',
				error,
				{ feature: 'analytics', operation: 'fetchCardData' }
			);
		}
	}

	/**
	 * Load page config using async/await.
	 */
	private async loadPageConfig(): Promise<void> {
		const aid = this.accountId();
		const pid = this.pageId();

		if (!aid || !pid) return;

		this.pageLoading.set(true);

		try {
			const pageConfig = await this.analyticsService.fetchPageConfig(
				aid,
				pid
			);

			this.pageService.updatePageConfig(pid, pageConfig);

			const cardsToLoad = requirePageLayout({
				pageId: pageConfig.pageId,
				layout: pageConfig.layout,
			});
			if (cardsToLoad.length > 0) {
				const layout: LayoutConfig = {
					layoutId: deriveLayoutIdForPage(pageConfig.pageId),
					pageId: pageConfig.pageId,
					order: pageConfig.order ?? 1,
					cards: cardsToLoad,
				};
				this.layoutService.setLayoutConfig(layout);
				if (this.viewConfig()) {
					this.getCardConfigs(
						this.viewConfig()!.accountId,
						pid,
						cardsToLoad
					);
				}
			}

			const currentViewConfig = this.viewConfig();
			if (currentViewConfig) {
				const pageIndex = currentViewConfig.pages.findIndex(
					page => page.pageId === pid
				);

				// Mutating the config, then setting the signal to trigger updates
				// Ideally we'd clone, but sticking to existing pattern for now
				if (pageIndex >= 0) {
					currentViewConfig.pages[pageIndex] =
						pageConfig as PageConfig;
				} else {
					currentViewConfig.pages.push(pageConfig as PageConfig);
				}
				this.viewConfig.set({ ...currentViewConfig });
			}
			const pageCards = requirePageLayout({
				pageId: pageConfig.pageId,
				layout: pageConfig.layout,
			});
			this.captureReadiness.beginPage(pageCards);
		} catch (error) {
			reportAnalyticsError(
				'[Analytics] Error loading page config:',
				error,
				{ feature: 'analytics', operation: 'loadPageConfig' }
			);
		} finally {
			this.pageLoading.set(false);
		}
	}

	onFilterDateRangeChange(expression: DateRangeExpression): void {
		this.activePageDateRangeExpression.set(expression);
		const [start, end] = dateRangeExpressionForPageData(expression);
		this.selectedDateRange.set([start, end]);
		// Debounce getPageData through the same 150ms path as page filters (JEP-10271).
		this.pageFilterService.refresh$.next();
		this.loadDropdownFilterCardDataForCurrentPage();
	}

	private loadDropdownFilterCardDataForCurrentPage(): void {
		const viewComp = this.viewComponent();
		const selectedPageId = viewComp?.selectedPageIdSignal();
		const currentViewConfig = this.viewConfig();

		if (!selectedPageId || !currentViewConfig) {
			return;
		}

		const selectedPage = currentViewConfig.pages.find(
			page => this.normalizePageId(page.pageId) === selectedPageId
		);

		if (!selectedPage) {
			return;
		}

		this.loadDropdownFilterCardData(requirePageLayout(selectedPage));
	}

	/**
	 * Resolves `getPageData` / `getCardData` date range for shared reports.
	 * Dropdown-only pages never sync {@link FilterService}, so fall back to view
	 * `dateRange` and page filter defaults (same as the main report editor).
	 */
	private resolvePageDateRangeForFetch(): string[] | undefined {
		const fromPicker = this.selectedDateRange();
		if (fromPicker?.length === 2 && fromPicker[0] && fromPicker[1]) {
			return fromPicker;
		}

		const expression = this.resolvePageDateRangeExpression();
		if (!expression) {
			return undefined;
		}

		const [start, end] = dateRangeExpressionForPageData(expression);
		if (!start || !end) {
			return undefined;
		}
		return [start, end];
	}

	private resolvePageDateRangeExpression(): DateRangeExpression | undefined {
		const config = this.viewConfig();
		const currentPageId = this.pageId();
		if (!config) {
			return undefined;
		}

		const page = config.pages?.find(
			p => currentPageId && this.isSamePage(p.pageId, currentPageId)
		);
		const layout = page ? requirePageLayout(page) : [];
		const viewDateRange = parseViewDateRangeFromApi(config.dateRange);
		const reportDefault = resolveReportDefaultDateRangeExpression(
			viewDateRange,
			page
		);
		const sessionExpression =
			this.activePageDateRangeExpression() ??
			this.filterService.dateRangeExpression() ??
			undefined;

		return resolvePageGlobalDateRangeExpression(
			layout,
			sessionExpression,
			reportDefault,
			cardId =>
				this.cardService.cardConfig(cardId)() as
					| FilterCardConfig
					| undefined
		);
	}

	/** Unique data-source slugs across all cards on the given page (for Sentry span tagging). */
	#dataSourceServicesForPage(pageId: string): string[] {
		const page = this.viewConfig()?.pages?.find(p => p.pageId === pageId);
		if (!page) {
			return [];
		}
		const services: string[] = [];
		for (const card of requirePageLayout(page)) {
			const config = this.cardService.cardConfig(card.cardId)();
			if (config && 'sources' in config) {
				const sources = (
					config as { sources?: { dataSource?: string }[] }
				).sources;
				for (const source of sources ?? []) {
					const slug = source.dataSource?.trim();
					if (slug && !services.includes(slug)) {
						services.push(slug);
					}
				}
			}
		}
		return services;
	}

	private async refreshPageData(
		options?: FetchPageDataOptions & {
			pageFilterRefreshGeneration?: number;
		}
	): Promise<void> {
		const aid = this.accountId();
		const pid = this.pageId();
		const range = this.resolvePageDateRangeForFetch();
		if (!aid || !pid || !range) {
			return;
		}

		const {
			pageFilterRefreshGeneration: refreshGeneration,
			...fetchOptions
		} = options ?? {};
		const isPageFilterRefresh =
			fetchOptions.requestId != null || refreshGeneration != null;
		const loadersAlreadyStarted = fetchOptions.requestId != null;
		const loadingCardIds = isPageFilterRefresh
			? this.#cardIdsForPageFilterRefresh(pid)
			: this.#cardIdsForPageDataRefresh(pid);
		if (!loadersAlreadyStarted && loadingCardIds.length > 0) {
			this.cardRuntimeRefreshState.beginCardsRefresh(loadingCardIds);
			this.appRef.tick();
		}

		try {
			await this.analyticsService.fetchPageData(
				aid,
				pid,
				{ dateRange: range },
				this.pageFilterService.pageFiltersForQuery(),
				{
					...fetchOptions,
					advanceWebSocketFloor:
						fetchOptions.requestId != null
							? false
							: fetchOptions.advanceWebSocketFloor,
					dataSourceServices: this.#dataSourceServicesForPage(pid),
				}
			);
		} catch (error) {
			if (
				refreshGeneration == null ||
				refreshGeneration === this.#pageFilterRefreshGeneration
			) {
				this.cardRuntimeRefreshState.endCardsRefresh(loadingCardIds);
			}
			reportAnalyticsError(
				'[Analytics] Error fetching page data:',
				error,
				{ feature: 'analytics', operation: 'fetchPageData' }
			);
		}
	}

	/** Chart/table cards on the page — all receive filtered getPageData batches. */
	#cardIdsForPageFilterRefresh(pageId: string): string[] {
		const page = this.viewConfig()?.pages?.find(p => p.pageId === pageId);
		if (!page) {
			return [];
		}
		const cardIds: string[] = [];
		for (const card of requirePageLayout(page)) {
			if (
				card.cardType !== CardType.Chart &&
				card.cardType !== CardType.Table
			) {
				continue;
			}
			cardIds.push(card.cardId);
		}
		return cardIds;
	}

	/** Chart/table cards that should show loaders while page data refetches. */
	#cardIdsForPageDataRefresh(pageId: string): string[] {
		const page = this.viewConfig()?.pages?.find(p => p.pageId === pageId);
		if (!page) {
			return [];
		}
		const cardIds: string[] = [];
		for (const card of requirePageLayout(page)) {
			if (
				card.cardType !== CardType.Chart &&
				card.cardType !== CardType.Table
			) {
				continue;
			}
			const full = this.cardService.cardConfig(card.cardId)();
			if (!shouldRefetchCardDataOnGlobalDateChange(full ?? card)) {
				continue;
			}
			cardIds.push(card.cardId);
		}
		return cardIds;
	}

	private loadDropdownFilterCardData(cards: CardList[]): void {
		const range = this.resolvePageDateRangeForFetch();
		const aid = this.accountId();
		if (!range || !aid) {
			return;
		}

		for (const card of cards) {
			if (card.cardType !== CardType.Filter) {
				continue;
			}
			const config = this.cardService.cardConfig(card.cardId)() as
				| FilterCardConfig
				| undefined;
			let controlType: ReturnType<
				typeof resolveFilterControlType
			> | null = null;
			try {
				controlType = resolveFilterControlType({
					layout: card,
					config,
				});
			} catch (error) {
				warn(
					'[Analytics] Skipping filter data hydration for missing controlType',
					{ cardId: card.cardId, error }
				);
				continue;
			}
			if (!isDropdownFilterControlType(controlType)) {
				continue;
			}
			void this.getCardData(card.cardId, range);
		}
	}

	public normalizePageId(pageId: string): string {
		if (!pageId) return '';
		return pageId.startsWith('page_') ? pageId.substring(5) : pageId;
	}

	private ensurePagePrefix(pageId: string): string {
		if (!pageId) return '';
		return pageId.startsWith('page_') ? pageId : `page_${pageId}`;
	}

	private isSamePage(pageId1: string, pageId2: string): boolean {
		return this.normalizePageId(pageId1) === this.normalizePageId(pageId2);
	}

	private handlePalettes(palettes: PaletteConfig[]): void {
		// Always merge built-in palettes so the app has them without the API returning them.
		const apiPalettes = palettes ?? [];
		const palettesWithDefaults = [
			...BUILT_IN_PALETTES,
			...apiPalettes.filter(p => !isBuiltInPaletteId(p.paletteId)),
		];
		this.paletteService.setAvailablePalettes(palettesWithDefaults);
	}

	/**
	 * Debug helper - exposes card state debugging to window console
	 * Call window.debugCardStates() in browser console to inspect
	 */
	private setupDebugHelpers(): void {
		(window as any).debugCardStates = () => {
			log('[Analytics] Debug: Current card states');
			const allCards = this.cardService.allCards();
			allCards.forEach(card => {
				log(`Card ${card.cardId}:`, {
					cardType: card.cardType,
					title: card.title,
					hasContent: !!card.content,
					hasHtml: !!(card as any).html,
					hasMarkdown: !!(card as any).markdown,
					fullCard: card,
				});
			});
		};

		(window as any).forceCardUpdates = () => {
			log('[Analytics] Forcing card updates');
			const allCards = this.cardService.allCards();
			allCards.forEach(card => {
				this.cardService.updateCardConfig(card.cardId, { ...card });
			});
		};
	}

	/** Table slices live on {@link DataService}, not on {@link TableCardConfig}.data. */
	private tableCardHasLoadedData(cardId: string): boolean {
		const fromTable = this.dataService.tableDataSignal(cardId)();
		if (fromTable && hasRenderableTablePayload(fromTable)) {
			return true;
		}
		const fromChart = tableDataFromChartDataResponse(
			this.dataService.cardDataSignal(cardId)(),
			cardId
		);
		return !!(fromChart && hasRenderableTablePayload(fromChart));
	}

	async onDownloadPdfClick(): Promise<void> {
		const accountId = this.accountId();
		const viewId = this.viewId();
		if (!accountId || !viewId || this.pdfExportInProgress()) {
			return;
		}

		this.pdfExportInProgress.set(true);
		try {
			await this.reportPdfExportService.downloadReportPdf(
				accountId,
				viewId
			);
		} catch (exportError: unknown) {
			const message =
				exportError instanceof Error
					? exportError.message
					: PDF_EXPORT_GENERIC_ERROR;
			this.snackBar.openFromComponent(JepSnackbarComponent, {
				duration: 6000,
				verticalPosition: 'bottom',
				horizontalPosition: 'center',
				data: { text: message, status: 'error' },
			});
		} finally {
			this.pdfExportInProgress.set(false);
		}
	}
}
